/**
 * AccessibilitySystem — user-facing accessibility settings.
 *
 * Storage: its own localStorage slot, independent of run progress so a
 * player who clears their save still keeps their a11y preferences.
 *
 * Settings:
 *   - reducedMotion — kills screen shake, limits particle density, and
 *     damps camera lerps. Requested by players with vestibular issues
 *     and anyone who finds the cinematic shakes nauseating.
 *   - colorblindMode — palette override. When not "none" the interactive
 *     outline and ability-glow hues shift to a safe ramp. Three common
 *     deficiencies are named so the UI can expose the exact mode.
 *   - fontScale — multiplier applied to all dialogue / HUD text. 1.0 is
 *     the shipped default; 1.25 and 1.5 are accessibility presets.
 *   - highContrastUI — boosts HUD panel alpha + text shadow, swaps
 *     muted palette colors for saturated accents.
 *
 * API: getInstance() returns the singleton. Call `update(partial)` to
 * change any field; the system persists immediately and emits an
 * `ACCESSIBILITY_CHANGED` event so listeners can re-apply.
 */
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";

export type ColorblindMode =
  | "none"
  | "deuteranopia"     // red-green, most common
  | "protanopia"       // red weakness
  | "tritanopia";      // blue-yellow

export interface AccessibilitySettings {
  reducedMotion: boolean;
  colorblindMode: ColorblindMode;
  fontScale: number;
  highContrastUI: boolean;
  /**
   * Making Biscuits auto-knead assist. When true, the mini-game auto-fills
   * the trance depth meter over ~8 seconds after activation — the player
   * only has to enter biscuit mode and hold any paw key. Mirrors the
   * "Auto-Knead" assist mode in `docs/specs/SPEC-making-biscuits.md` §3.
   */
  autoKneadAssist: boolean;
  /**
   * Biscuits rhythm-window multiplier. 1.0× = shipping default (generous
   * 0.8s window). 1.5× and 2.0× widen the hit window for players who
   * struggle with timing. Clamped to [0.5, 3.0].
   */
  beatWindowMultiplier: number;
  /**
   * Controls how dialogue advances during cutscenes and Elder encounters.
   *   'auto'   — lines advance automatically after a dwell period scaled by
   *              `dialogueDwellMultiplier`. Matches the shipped default.
   *   'manual' — each line waits indefinitely for the player to press the
   *              advance key. Useful for slow readers or screen-reader users.
   */
  dialogueAdvance: "auto" | "manual";
  /**
   * Scales the auto-advance dwell time for each dialogue line.
   * 1.0× = default cadence; 2.0× doubles the reading window.
   * Clamped to [0.5, 3.0].
   */
  dialogueDwellMultiplier: number;
}

const STORAGE_KEY = "cats-of-chronos-a11y";

const DEFAULTS: AccessibilitySettings = {
  reducedMotion: false,
  colorblindMode: "none",
  fontScale: 1.0,
  highContrastUI: false,
  autoKneadAssist: false,
  beatWindowMultiplier: 1.0,
  dialogueAdvance: "auto",
  dialogueDwellMultiplier: 1.0,
};

/**
 * Colorblind-safe palette overrides. Applied to the `COLORS` namespace
 * at runtime when a mode is active. Only the hues that the game uses
 * for feedback (Minna aura, Basil harness, bioluminescent accents,
 * HUD progress) are overridden — the decorative scenery palette stays.
 *
 * The ramps below are picked from Okabe-Ito's 8-colour palette, which
 * is the de-facto standard for colorblind-safe scientific plotting.
 * Each mode shifts the in-game "emerald = minna" and "copper = basil"
 * poles onto two hues that are distinguishable under that deficiency.
 */
export const COLORBLIND_OVERRIDES: Record<
  ColorblindMode,
  Record<string, string>
> = {
  none: {},
  deuteranopia: {
    // Minna's green → sky blue (#0072B2). Basil's copper → vermillion
    // (#D55E00). These two poles remain distinct under deuteranopia.
    MINNA_AURA: "#0072B2",
    BIOLUMINESCENT_GREEN: "#56B4E9",
    BIOLUMINESCENT_BLUE: "#0072B2",
    BASIL_HARNESS: "#D55E00",
    COPPER: "#D55E00",
    BRASS: "#E69F00",
    EMERALD: "#56B4E9",
    // ChronosBubble violet → sky blue (deuteranopia confuses red-green,
    // violet reads as blue which is safe; use sky-blue variant for extra
    // distinctness from the darker #0072B2 used for Minna's aura).
    BIOLUMINESCENT_PURPLE: "#56B4E9",
    // Basil's HUD dot — vermillion matches BASIL_HARNESS pole.
    HUD_BASIL: "#D55E00",
  },
  protanopia: {
    MINNA_AURA: "#0072B2",
    BIOLUMINESCENT_GREEN: "#56B4E9",
    BIOLUMINESCENT_BLUE: "#0072B2",
    BASIL_HARNESS: "#E69F00",
    COPPER: "#E69F00",
    BRASS: "#F0E442",
    EMERALD: "#56B4E9",
    // ChronosBubble — sky blue, distinct from both yellow poles.
    BIOLUMINESCENT_PURPLE: "#56B4E9",
    // Basil's HUD dot — golden yellow matches BASIL_HARNESS pole.
    HUD_BASIL: "#E69F00",
  },
  tritanopia: {
    // Tritanopia is blue-yellow confusion. Shift Minna's blue-green
    // aura toward magenta and keep Basil warm.
    MINNA_AURA: "#CC79A7",
    BIOLUMINESCENT_GREEN: "#CC79A7",
    BIOLUMINESCENT_BLUE: "#CC79A7",
    BASIL_HARNESS: "#D55E00",
    COPPER: "#D55E00",
    BRASS: "#E69F00",
    EMERALD: "#CC79A7",
    // ChronosBubble — magenta/pink, same family as Minna's tritanopia
    // aura so the "time field" reads as a Minna-affiliated effect.
    BIOLUMINESCENT_PURPLE: "#CC79A7",
    // Basil's HUD dot — vermillion, same as BASIL_HARNESS pole.
    HUD_BASIL: "#D55E00",
  },
};

export class AccessibilitySystem {
  private static instance: AccessibilitySystem | null = null;

  static getInstance(): AccessibilitySystem {
    if (!AccessibilitySystem.instance) {
      AccessibilitySystem.instance = new AccessibilitySystem();
    }
    return AccessibilitySystem.instance;
  }

  private settings: AccessibilitySettings;

  private constructor() {
    this.settings = this.load();
  }

  private load(): AccessibilitySettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<AccessibilitySettings>;
        return { ...DEFAULTS, ...parsed };
      }
    } catch {
      // Corrupted or unavailable — fall through to defaults.
    }
    return { ...DEFAULTS };
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // private-browsing quota or no storage — silently ignore.
    }
  }

  /** Current settings snapshot. */
  get state(): Readonly<AccessibilitySettings> {
    return this.settings;
  }

  /** Partial update — merges with current settings, persists, emits event. */
  update(partial: Partial<AccessibilitySettings>): void {
    this.settings = { ...this.settings, ...partial };
    // Clamp font scale to a sane range.
    if (this.settings.fontScale < 0.8) this.settings.fontScale = 0.8;
    if (this.settings.fontScale > 2.0) this.settings.fontScale = 2.0;
    // Clamp biscuits rhythm multiplier. 1.0× is the shipping default;
    // 0.5× is for the truly rhythm-confident, 3.0× is the outer bound of
    // "still a mini-game rather than a cutscene."
    if (this.settings.beatWindowMultiplier < 0.5) this.settings.beatWindowMultiplier = 0.5;
    if (this.settings.beatWindowMultiplier > 3.0) this.settings.beatWindowMultiplier = 3.0;
    // Clamp dialogue dwell multiplier — 0.5× minimum keeps auto-advance
    // usable; 3.0× maximum keeps it recognisably automatic rather than manual.
    if (this.settings.dialogueDwellMultiplier < 0.5) this.settings.dialogueDwellMultiplier = 0.5;
    if (this.settings.dialogueDwellMultiplier > 3.0) this.settings.dialogueDwellMultiplier = 3.0;
    this.persist();
    gameEvents.emit(GameEvents.ACCESSIBILITY_CHANGED, this.settings);
  }

  /** Resolve a named color through the current colorblind override. */
  resolveColor(key: string, fallback: string): string {
    const override = COLORBLIND_OVERRIDES[this.settings.colorblindMode]?.[key];
    return override ?? fallback;
  }
}
